import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { API_URL } from '../config/api';
import {
  ShoppingCart, Loader2, Star, ShoppingBag,
  Home, ChevronRight, ChevronLeft, TrendingUp, Flame, Zap, Clock, 
  ArrowRight, CheckCircle2, Tag, CreditCard, Award, Truck, Headset, Lock, X, Copy, Check
} from 'lucide-react';

const StorePage = () => {
  const navigate = useNavigate();
  const { categoryName } = useParams();
  const { addToCart } = useCart();
  const [searchParams] = useSearchParams();

  const selectedCategory = categoryName || searchParams.get('category') || 'All';
  const isCategoryPage = !!categoryName;
  const searchQuery = searchParams.get('q') || '';

  const [products, setProducts] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [dbCategories, setDbCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topProductsLoading, setTopProductsLoading] = useState(true);
  const [addedProductId, setAddedProductId] = useState(null);

  // Interactive Vault State
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [vaultCopied, setVaultCopied] = useState(false);

  // Real-time Deal Countdown Timer State
  const [timeLeft, setTimeLeft] = useState({ hours: 4, minutes: 28, seconds: 45 });

  // Hero Product Slideshow & HD Image Preview State
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const [isHeroPaused, setIsHeroPaused] = useState(false);
  const [previewModalProduct, setPreviewModalProduct] = useState(null);

  // Helper: Verify product is in stock
  const isInStock = (p) => {
    if (!p) return false;
    if (p.in_stock === false) return false;
    if (p.total_available !== undefined && p.total_available <= 0) return false;
    if (p.stock_quantity !== undefined && p.stock_quantity <= 0) return false;
    if (p.stock !== undefined && p.stock <= 0) return false;
    if (p.is_out_of_stock === true) return false;
    return true;
  };

  // Helper: Get numeric stock count from whichever field the API returns
  const getStock = (p) => {
    if (!p) return 0;
    if (p.total_available !== undefined) return Number(p.total_available) || 0;
    if (p.stock_quantity  !== undefined) return Number(p.stock_quantity)  || 0;
    if (p.stock           !== undefined) return Number(p.stock)           || 0;
    return p.in_stock ? 1 : 0;
  };

  // Dynamic products list for Hero Slideshow (STRICTLY IN-STOCK PRODUCTS ONLY)
  const inStockTop = topProducts.filter(isInStock);
  const inStockAll = products.filter(isInStock);
  const heroProducts = (inStockTop.length > 0 ? inStockTop : (inStockAll.length > 0 ? inStockAll : []));

  // Curated Selection: products sorted by stock descending (highest stock first)
  const topStockProducts = [...products]
    .filter(p => p && p.id)
    .sort((a, b) => getStock(b) - getStock(a))
    .slice(0, 12);

  // Auto-advance hero product slideshow every 3s
  useEffect(() => {
    if (isHeroPaused || heroProducts.length <= 1) return;
    const interval = setInterval(() => {
      setHeroSlideIndex(prev => (prev + 1) % heroProducts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isHeroPaused, heroProducts.length]);

  useEffect(() => {
    let isMounted = true;
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/categories`);
        const type = res.headers.get('content-type') || '';
        if (res.ok && type.includes('application/json')) {
          const json = await res.json();
          if (json.success && Array.isArray(json.data) && json.data.length > 0) {
            if (isMounted) setDbCategories(json.data);
            return;
          }
        }

        const filterRes = await fetch(`${API_URL}/api/products/filters/options`);
        const filterType = filterRes.headers.get('content-type') || '';
        if (filterRes.ok && filterType.includes('application/json')) {
          const filterJson = await filterRes.json();
          if (filterJson.success && filterJson.filters?.categories && filterJson.filters.categories.length > 0) {
            const parsed = filterJson.filters.categories.map(c => ({
              id: c,
              name: c,
              description: `Explore premium ${c} products`
            }));
            if (isMounted) setDbCategories(parsed);
          }
        }
      } catch (err) {
        // Fallback maintained smoothly
      }
    };
    fetchCategories();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: 59, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return { hours: 5, minutes: 0, seconds: 0 };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchProducts();
    if (!isCategoryPage) {
      fetchTopProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, searchQuery, isCategoryPage]);

  const fetchTopProducts = async () => {
    try {
      setTopProductsLoading(true);
      const response = await fetch(`${API_URL}/api/products?sortBy=newest`);
      const result = await response.json();
      if (result.success) {
        setTopProducts(result.data || []);
      } else {
        setTopProducts([]);
      }
    } catch (err) {
      console.error('❌ Error fetching top products:', err);
      setTopProducts([]);
    } finally {
      setTopProductsLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory !== 'All' && selectedCategory !== 'other') {
        params.append('category', selectedCategory);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      // No limit — fetch all products
      
      const response = await fetch(`${API_URL}/api/products?${params.toString()}`);
      const result = await response.json();
      if (result.success) {
        setProducts(result.data || []);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const result = await addToCart(product);
    if (result && !result.redirected && result.error) {
      alert(`❌ ${result.error}`);
    } else {
      setAddedProductId(product.id);
      setTimeout(() => setAddedProductId(null), 1500);
    }
  };

  // ── CATEGORY VIEW PAGE (LIGHT LUXURY) ──────────────────────────────────────
  if (isCategoryPage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900 bg-dot-grid-light py-8 font-sans relative overflow-hidden">
        
        {/* Ambient Gradient Orbs */}
        <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-gradient-to-br from-orange-400/20 via-amber-500/10 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[600px] h-[600px] bg-gradient-to-tr from-blue-400/20 via-indigo-500/10 to-transparent rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 py-4 text-xs font-black uppercase tracking-widest text-slate-500">
            <Link to="/" className="hover:text-orange-600 flex items-center gap-1"><Home className="w-3.5 h-3.5" /> Home</Link>
            <ChevronRight className="w-3 h-3 opacity-40" />
            <span className="text-orange-600 font-extrabold">{selectedCategory}</span>
          </div>

          {/* Category Banner */}
          <div className="liquid-glass rounded-3xl p-8 mb-10 text-slate-900 flex flex-col md:flex-row items-center justify-between gap-6 border border-slate-200/90 shadow-xl bg-white/90">
            <div>
              <span className="px-3.5 py-1 rounded-full bg-orange-100 text-orange-700 border border-orange-200 text-xs font-black uppercase tracking-widest">
                Sector Hub
              </span>
              <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight mt-3 text-slate-900">
                {selectedCategory}
              </h1>
              <p className="text-sm text-slate-500 mt-2">Showing {products.length} live products in this sector</p>
            </div>
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-orange-500/15 to-blue-600/15 flex items-center justify-center border border-slate-200">
              <ShoppingBag className="w-10 h-10 text-orange-600 animate-pulse" />
            </div>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="py-32 flex flex-col items-center justify-center">
              <Loader2 className="w-16 h-16 text-orange-600 animate-spin" />
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest mt-6">Loading category products...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-16">
              {products.length > 0 ? products.map((p, i) => (
                <Link 
                  key={p.id || i} 
                  to={`/products/${p.id}`}
                  className="bg-white/90 rounded-3xl overflow-hidden border border-slate-200/90 hover:border-orange-500/50 transition-all duration-500 group flex flex-col hover:-translate-y-2 hover:shadow-2xl hover:shadow-slate-300/50 relative"
                >
                  <div className="h-56 bg-white p-4 flex items-center justify-center relative overflow-hidden">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500 product-img-clean" />
                    ) : (
                      <ShoppingBag className="w-16 h-16 text-slate-300" />
                    )}
                    <span className="absolute top-3 left-3 text-[9px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/90 text-orange-700 border border-orange-200 shadow-sm">
                      {p.category}
                    </span>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-sm font-black text-slate-900 uppercase line-clamp-2 mb-2 group-hover:text-orange-600 transition-colors">
                      {p.name}
                    </h3>
                    <div className="flex items-center gap-1 mb-3 text-xs text-amber-500">
                      {[...Array(5)].map((_, s) => <Star key={s} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                      <span className="text-[11px] text-slate-500 ml-1 font-bold">({p.average_rating || 4.9})</span>
                    </div>
                    <div className="flex items-baseline gap-2 mb-4 mt-auto">
                      <span className="text-2xl font-black text-slate-900">₹{(p.price || 0).toLocaleString()}</span>
                      <span className="text-xs text-slate-400 line-through">₹{Math.round((p.price || 0) * 1.25).toLocaleString()}</span>
                    </div>
                    {p.in_stock ? (
                      <button
                        onClick={(e) => handleAddToCart(e, p)}
                        className="w-full skeuo-btn py-3 rounded-2xl text-white text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        {addedProductId === p.id ? 'ADDED TO CART! ✓' : 'ADD TO CART'}
                      </button>
                    ) : (
                      <button disabled className="w-full py-3 bg-slate-200 text-slate-500 text-xs font-black uppercase rounded-2xl cursor-not-allowed">
                        OUT OF STOCK
                      </button>
                    )}
                  </div>
                </Link>
              )) : (
                <div className="col-span-full py-24 text-center">
                  <ShoppingBag className="w-20 h-20 text-slate-300 mx-auto mb-4" />
                  <h4 className="text-xl font-black text-slate-400 uppercase">No Products Found</h4>
                  <button onClick={() => navigate('/')} className="mt-4 skeuo-btn text-white px-6 py-2.5 rounded-2xl text-xs font-bold uppercase">
                    Back to Home
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── 7-SECTION LIGHT LUXURY HOME PAGE ───────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900 bg-dot-grid-light font-sans relative overflow-hidden">

      {/* Floating Ambient Orbs */}
      <div className="absolute top-[-10%] left-[-5%] w-[600px] h-[600px] bg-gradient-to-br from-orange-400/20 via-amber-500/10 to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-5%] w-[700px] h-[700px] bg-gradient-to-tr from-blue-400/20 via-indigo-500/10 to-transparent rounded-full blur-3xl pointer-events-none" />

      {/* ── HERO SECTION matching input_file_0.png reference ── */}
      <section className="relative pt-4 pb-10 overflow-hidden">
        <div className="max-w-[1536px] w-full mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
            
            {/* Left Hero Card (8 Cols) - HYPER VIBRANT LIGHT LUXURY SHOWCASE */}
            <div 
              className="lg:col-span-8 bg-gradient-to-br from-orange-50/80 via-white to-amber-50/60 rounded-[2.5rem] p-6 sm:p-8 md:p-9 border-2 border-amber-300/80 shadow-xl relative overflow-hidden flex flex-col justify-between min-h-[560px] text-slate-900 group/hero"
              onMouseEnter={() => setIsHeroPaused(true)}
              onMouseLeave={() => setIsHeroPaused(false)}
            >
              {/* Dot Grid Background */}
              <div className="absolute inset-0 bg-dot-grid-light opacity-60 pointer-events-none" />

              {/* Soft Ambient Radial Background Aura */}
              <div className="absolute top-0 right-0 w-[450px] h-[450px] bg-gradient-to-br from-amber-200/40 via-orange-300/30 to-transparent rounded-full blur-3xl pointer-events-none" />

              {/* Top Tagline Strip */}
              <div className="relative z-10 flex flex-wrap items-center justify-between gap-3 mb-2">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-orange-600 via-amber-500 to-orange-500 text-white text-[11px] font-black uppercase tracking-wider shadow-md shadow-orange-500/20">
                  <Flame className="w-4 h-4 text-amber-200 animate-pulse" />
                  <span>MEGA FESTIVAL SALE IS LIVE</span>
                </div>
                
                {/* Realtime Live Counter Pill */}
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/90 border border-orange-200 text-slate-800 text-[10px] font-bold shadow-xs animate-pulse-glow">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                  <span>LIVE STOCK DROP • 84% CLAIMED</span>
                </div>
              </div>

              {/* Active Product Slide Content */}
              {(() => {
                const currentProduct = heroProducts[heroSlideIndex] || {
                  id: 'default',
                  name: 'PRODUCT NAME',
                  category: 'FURNITURE',
                  description: 'Short description of the product goes here. Highlight key benefits and value.',
                  price: 1234,
                  image_url: ''
                };

                const targetLink = currentProduct.id && currentProduct.id !== 'default' 
                  ? `/products/${currentProduct.id}` 
                  : (currentProduct.category ? `/categories/${encodeURIComponent(currentProduct.category)}/products` : '/');
                const originalPrice = Math.round((currentProduct.price || 0) * 1.25);
                const discountPct = Math.round(((originalPrice - (currentProduct.price || 0)) / originalPrice) * 100);

                const rawName = currentProduct.name || 'PRODUCT NAME';
                // Clean name without bracketed model code for main title
                const cleanName = rawName.replace(/\s*\([^)]*\)/g, '').trim() || 'PRODUCT NAME';
                const modelCode = (rawName.match(/\(([^)]+)\)/) || [])[1];

                const nameWords = cleanName.split(' ');
                const firstHalf = nameWords.slice(0, Math.ceil(nameWords.length / 2)).join(' ');
                const secondHalf = nameWords.slice(Math.ceil(nameWords.length / 2)).join(' ');

                // Formatted description showing model below
                const formattedDescription = modelCode
                  ? `Model: ${modelCode}${currentProduct.description ? ` • ${currentProduct.description}` : ''}`
                  : (currentProduct.description || 'Short description of the product goes here. Highlight key benefits and value.');

                return (
                  <div key={currentProduct.id || heroSlideIndex} className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center flex-1 my-auto animate-fade-in py-2">
                    
                    {/* Left Info Column (7 Cols) */}
                    <div className="md:col-span-7 space-y-3.5 text-left">
                      
                      {/* Category Badge */}
                      <div>
                        <span className="px-4 py-1.5 rounded-full bg-white border border-orange-400 text-orange-600 text-[11px] font-black uppercase tracking-widest inline-block shadow-xs">
                          {currentProduct.category?.toUpperCase() || 'FURNITURE'}
                        </span>
                      </div>

                      {/* Two-Tone Title */}
                      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[1.05] text-slate-900">
                        {firstHalf || 'PRODUCT'} <br />
                        <span className="text-[#F97316]">{secondHalf || 'NAME'}</span>
                      </h1>

                      {/* Description / Model Details */}
                      <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed max-w-md line-clamp-3">
                        {formattedDescription}
                      </p>

                      {/* Price Capsule & Discount Badge */}
                      <div className="flex flex-wrap items-center gap-3 pt-1">
                        <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-orange-500 text-white px-6 py-3.5 rounded-2xl flex items-baseline gap-3 shadow-lg shadow-orange-500/25">
                          <span className="text-2xl sm:text-3xl font-black text-amber-100 font-mono tracking-tight">
                            ₹{Number(currentProduct.price || 1234).toLocaleString()}
                          </span>
                          {currentProduct.price && (
                            <span className="text-xs sm:text-sm text-orange-200 line-through font-mono">
                              ₹{originalPrice.toLocaleString()}
                            </span>
                          )}
                        </div>

                        {discountPct > 0 && (
                          <span className="px-5 py-3.5 rounded-2xl bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white text-xs font-black uppercase tracking-wider shadow-md">
                            SAVE {discountPct}% OFF
                          </span>
                        )}
                      </div>

                      {/* Feature Pills */}
                      <div className="flex flex-wrap items-center gap-2.5 pt-1">
                        <div className="bg-white border border-slate-200 px-3.5 py-1.5 rounded-xl text-[11px] font-bold text-slate-700 shadow-2xs flex items-center gap-2">
                          <Truck className="w-3.5 h-3.5 text-blue-600" />
                          <span>Express Dispatch</span>
                        </div>
                        <div className="bg-white border border-slate-200 px-3.5 py-1.5 rounded-xl text-[11px] font-bold text-slate-700 shadow-2xs flex items-center gap-2">
                          <Award className="w-3.5 h-3.5 text-emerald-600" />
                          <span>BVP Guaranteed</span>
                        </div>
                      </div>

                    </div>

                    {/* Right Column — Product Image Card */}
                    <div className="md:col-span-5 flex flex-col justify-center relative w-full h-full">

                      <Link
                        to={targetLink}
                        className="group/img relative w-full rounded-3xl overflow-hidden bg-gradient-to-br from-white via-slate-50 to-orange-50/40 border border-slate-200 hover:border-orange-400 shadow-lg hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-500 flex items-center justify-center"
                        style={{ minHeight: '320px' }}
                      >
                        {/* Subtle radial glow behind image */}
                        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(249,115,22,0.07)_0%,_transparent_70%)] pointer-events-none" />

                        {currentProduct.image_url ? (
                          <img
                            src={currentProduct.image_url}
                            alt={currentProduct.name}
                            className="relative z-10 w-full h-full object-contain p-8 group-hover/img:scale-105 transition-transform duration-500 drop-shadow-xl product-img-clean"
                            style={{ maxHeight: '320px' }}
                          />
                        ) : (
                          <ShoppingBag className="w-20 h-20 text-slate-300 animate-float-bob" />
                        )}

                        {/* Bottom name strip */}
                        <div className="absolute bottom-0 left-0 right-0 px-5 py-3 bg-gradient-to-t from-white/90 via-white/60 to-transparent flex items-end justify-between z-20">
                          <span className="text-[11px] font-black uppercase text-slate-700 truncate max-w-[70%] tracking-wide">
                            {currentProduct.name?.replace(/\s*\([^)]*\)/g, '').trim()}
                          </span>
                          <span className="text-[11px] font-black text-orange-600 bg-orange-50 border border-orange-200 px-2.5 py-1 rounded-full shadow-xs">
                            ₹{Number(currentProduct.price || 0).toLocaleString()}
                          </span>
                        </div>
                      </Link>

                    </div>

                  </div>
                );
              })()}

              {/* Bottom Footer Bar */}
              <div className="pt-6 border-t border-slate-200/60 flex flex-wrap items-center justify-between gap-4 mt-4 relative z-10">
                
                {/* Countdown Timer */}
                <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                  <Clock className="w-4 h-4 text-orange-600 animate-pulse" />
                  <span className="uppercase font-black text-slate-800">ENDS IN:</span>
                  <div className="flex items-center gap-1 font-mono text-xs font-bold ml-1">
                    <span className="px-2.5 py-1.5 bg-gradient-to-br from-orange-500 to-amber-600 text-white rounded-lg shadow-md">
                      {String(timeLeft.hours).padStart(2, '0')}h
                    </span>
                    <span className="text-orange-600 font-black">:</span>
                    <span className="px-2.5 py-1.5 bg-gradient-to-br from-orange-500 to-amber-600 text-white rounded-lg shadow-md">
                      {String(timeLeft.minutes).padStart(2, '0')}m
                    </span>
                    <span className="text-orange-600 font-black">:</span>
                    <span className="px-2.5 py-1.5 bg-gradient-to-br from-orange-500 to-amber-600 text-white rounded-lg shadow-md">
                      {String(timeLeft.seconds).padStart(2, '0')}s
                    </span>
                  </div>
                </div>

                {/* Action CTA Buttons */}
                {(() => {
                  const currentProduct = heroProducts[heroSlideIndex];
                  const targetLink = currentProduct?.id && currentProduct.id !== 'default' 
                    ? `/products/${currentProduct.id}` 
                    : (currentProduct?.category ? `/categories/${encodeURIComponent(currentProduct.category)}/products` : '/');
                  return (
                    <div className="flex items-center gap-3">
                      {currentProduct && currentProduct.id && currentProduct.id !== 'default' && (
                        <button
                          onClick={(e) => handleAddToCart(e, currentProduct)}
                          className="bg-white border border-slate-200/90 text-slate-900 text-xs font-black uppercase px-6 py-3.5 rounded-2xl flex items-center gap-2 shadow-xs hover:bg-slate-50 transition-all"
                        >
                          <ShoppingCart className="w-4 h-4 text-orange-600" />
                          <span>{addedProductId === currentProduct.id ? 'Added!' : 'Add to Cart'}</span>
                        </button>
                      )}
                      <Link 
                        to={targetLink}
                        className="bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white text-xs font-black uppercase px-7 py-3.5 rounded-2xl flex items-center gap-2 shadow-lg shadow-orange-500/20 hover:scale-105 transition-all"
                      >
                        <span>Shop the Drop</span>
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  );
                })()}

              </div>
            </div>

            {/* Right Column Bento Cards (4 Cols) - VIBRANT LIGHT LUXURY DECK */}
            <div className="lg:col-span-4 flex flex-col justify-between gap-4">
              
              {/* Bento Card 1: SAVINGS VAULT */}
              <div className="bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-amber-500/15 rounded-[2rem] p-6 border-2 border-amber-300/80 shadow-md flex flex-col justify-between min-h-[205px] relative overflow-hidden group hover:border-amber-400 transition-all">
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-2">
                    <span className="bg-amber-500 text-white px-3.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider shadow-xs">
                      SAVINGS VAULT
                    </span>
                    <span className="bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded-md text-[9px] font-mono font-bold hidden sm:inline-block">
                      VERIFIED 20M PASS
                    </span>
                  </div>
                  <Tag className="w-5 h-5 text-amber-600 group-hover:scale-125 transition-transform" />
                </div>

                <div className="relative z-10 my-2">
                  <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                    ₹1,000 MONTHLY REWARDS
                  </h3>

                  {/* Interactive Vault Unlock Box */}
                  <div className="mt-3">
                    {!vaultUnlocked ? (
                      <button
                        onClick={() => setVaultUnlocked(true)}
                        className="w-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 text-white font-black text-[11px] uppercase px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 shadow-md hover:scale-[1.02] active:scale-95 transition-all"
                      >
                        <Zap className="w-3.5 h-3.5 fill-white text-white animate-bounce" />
                        <span>UNLOCK VIP REWARD VAULT</span>
                      </button>
                    ) : (
                      <div className="bg-white/90 border border-amber-300 rounded-xl p-2.5 flex items-center justify-between gap-2 shadow-sm animate-fadeIn">
                        <div className="flex flex-col">
                          <span className="text-[8px] font-mono uppercase text-amber-800 font-bold">ACTIVE COUPON CODE</span>
                          <span className="text-xs font-mono font-black text-amber-900 tracking-wider">BVP-VAULT-20M</span>
                        </div>
                        <button
                          onClick={() => {
                            if (navigator.clipboard) {
                              navigator.clipboard.writeText('BVP-VAULT-20M');
                            }
                            setVaultCopied(true);
                            setTimeout(() => setVaultCopied(false), 2000);
                          }}
                          className="bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-black px-3 py-1.5 rounded-lg flex items-center gap-1 transition-all shadow-xs"
                        >
                          {vaultCopied ? <Check className="w-3 h-3 text-white" /> : <Copy className="w-3 h-3 text-white" />}
                          <span>{vaultCopied ? 'COPIED!' : 'COPY'}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer CTA */}
                <div className="flex items-center justify-between pt-2 border-t border-amber-200/80 relative z-10">
                  <Link 
                    to="/coupons" 
                    className="text-amber-800 font-extrabold text-xs flex items-center gap-1.5 hover:gap-2.5 transition-all group/link"
                  >
                    <span>My Coupons &amp; Offers</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                  <span className="text-[10px] font-mono text-emerald-600 font-bold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                    ACTIVE PASS
                  </span>
                </div>
              </div>

              {/* Bento Card 2: EXPRESS REGIONAL LOGISTICS HUB */}
              <div className="bg-gradient-to-br from-indigo-500/10 via-blue-500/5 to-purple-500/10 rounded-[2rem] p-6 border-2 border-indigo-200/80 shadow-md flex flex-col justify-between min-h-[205px] relative overflow-hidden group hover:border-indigo-400 transition-all">
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-2">
                    <span className="bg-indigo-600 text-white px-3.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider shadow-xs">
                      EXPRESS DISPATCH
                    </span>
                    <span className="bg-indigo-100 text-indigo-800 border border-indigo-200 px-2 py-0.5 rounded-md text-[9px] font-mono font-bold flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 animate-pulse" />
                      LIVE RADAR
                    </span>
                  </div>
                  <Truck className="w-5 h-5 text-indigo-600 group-hover:scale-125 transition-transform" />
                </div>

                <div className="relative z-10 my-2">
                  <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                    24-HOUR FAST SHIPMENT
                  </h3>
                  <p className="text-xs text-slate-600 mt-1 font-medium">Direct fulfillment from regional centers with live GPS tracking.</p>

                  {/* GPS Status Capsule */}
                  <div className="mt-3 bg-white/90 border border-indigo-200 rounded-xl p-2.5 flex items-center justify-between shadow-xs">
                    <span className="text-[9px] font-mono text-indigo-900 font-bold flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-indigo-600 animate-ping" />
                      LIVE DISPATCH NODE ACTIVE
                    </span>
                    <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 font-bold">
                      PRIORITY 1
                    </span>
                  </div>
                </div>

                {/* Footer CTA */}
                <div className="flex items-center justify-between pt-2 border-t border-indigo-200/80 relative z-10">
                  <Link 
                    to="/orders" 
                    className="text-indigo-800 font-extrabold text-xs flex items-center gap-1.5 hover:gap-2.5 transition-all group/link"
                  >
                    <span>Track Live Delivery</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                  <span className="text-[10px] font-mono text-indigo-700 font-bold">
                    HUB #04 READY
                  </span>
                </div>
              </div>

              {/* Bottom 3-Item Trust Strip */}
              <div className="bg-white rounded-[1.8rem] p-4 border border-slate-200/90 shadow-sm grid grid-cols-3 gap-2 text-center text-slate-700 font-bold">
                <div className="flex flex-col items-center gap-1">
                  <Lock className="w-4 h-4 text-slate-700" />
                  <span className="text-[10px] font-bold leading-tight">Secure Payments</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <Truck className="w-4 h-4 text-slate-700" />
                  <span className="text-[10px] font-bold leading-tight">Fast Delivery</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <Headset className="w-4 h-4 text-slate-700" />
                  <span className="text-[10px] font-bold leading-tight">24/7 Support</span>
                </div>
                </div>

            </div>

          </div>
        </div>
      </section>

      {/* ── SECTION 1: CYBERPUNK FLASH SALE & STOCK RUSH BAR ── */}
      <section className="py-8 relative z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="conic-border-box shadow-xl">
            <div className="conic-border-inner p-6 md:p-8 bg-white/95">
              
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-orange-100 border border-orange-200 flex items-center justify-center">
                    <Flame className="w-6 h-6 text-orange-600 animate-bounce" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
                      LIGHTNING FLASH DROP
                    </h2>
                    <p className="text-xs text-slate-500">Limited quantities remaining at maximum discounted rates</p>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="py-12 flex justify-center">
                  <Loader2 className="w-10 h-10 text-orange-600 animate-spin" />
                </div>
              ) : products.length === 0 ? (
                <div className="py-16 flex flex-col items-center gap-3">
                  <ShoppingBag className="w-14 h-14 text-slate-300" />
                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest">No Products in Inventory Yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                  {products.map((product, idx) => (
                    <Link
                      key={product.id || idx}
                      to={`/products/${product.id}`}
                      className="glass-panel rounded-2xl p-4 flex flex-col justify-between hover:border-orange-400 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group relative bg-white/80"
                    >


                      <div className="h-40 bg-white rounded-xl flex items-center justify-center p-3 mb-3 relative overflow-hidden border border-slate-100 shimmer-effect">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-md" />
                        ) : (
                          <ShoppingBag className="w-12 h-12 text-slate-300 animate-float-bob" />
                        )}
                      </div>

                      <div>
                        <span className="text-[9px] font-black uppercase tracking-wider text-orange-700 bg-orange-50 px-2 py-0.5 rounded-md border border-orange-100 inline-block mb-1">
                          {product.category}
                        </span>
                        <h3 className="text-xs font-black text-slate-900 uppercase line-clamp-2 mb-2 group-hover:text-orange-600 transition-colors">
                          {product.name}
                        </h3>
                        <div className="flex items-baseline gap-2 mb-3">
                          <span className="text-lg font-black text-orange-600">₹{(product.price || 0).toLocaleString()}</span>
                          <span className="text-xs text-slate-400 line-through">₹{Math.round((product.price || 0) * 1.3).toLocaleString()}</span>
                        </div>
                        {product.in_stock ? (
                          <button
                            onClick={(e) => handleAddToCart(e, product)}
                            className="w-full skeuo-btn py-2 rounded-xl text-white text-[11px] font-black uppercase tracking-wider flex items-center justify-center gap-1.5"
                          >
                            <ShoppingCart className="w-3.5 h-3.5" />
                            {addedProductId === product.id ? 'ADDED ✓' : 'CLAIM DEAL'}
                          </button>
                        ) : (
                          <button disabled className="w-full py-2 bg-slate-200 text-slate-500 text-[11px] font-black uppercase rounded-xl cursor-not-allowed">
                            SOLD OUT
                          </button>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}

            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 2: BENTO UI CATEGORY CYBER-HUB ── */}
      <section className="py-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4">
          
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="px-3.5 py-1 rounded-full bg-blue-100 text-blue-700 border border-blue-200 text-xs font-black uppercase tracking-widest">
              BENTO ORBIT
            </span>
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-slate-900 mt-2">
              BROWSE BY SECTOR
            </h2>
            <p className="text-xs text-slate-500 mt-1">Select a category node to filter live inventory</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {(dbCategories.length > 0 ? dbCategories : [
              { name: 'Technology', description: 'Laptops, Smart Gadgets & Gear' },
              { name: 'Home Tech', description: 'Smart Refrigerators & Washing Tech' },
              { name: 'Lifestyle', description: 'Inverters, Batteries & Furniture' },
              { name: 'Fashion', description: 'Trending Apparel & Accessories' },
            ]).map((cat, idx) => {
              const bgColors = [
                'bg-orange-50/80 border-orange-200',
                'bg-blue-50/80 border-blue-200',
                'bg-purple-50/80 border-purple-200',
                'bg-emerald-50/80 border-emerald-200',
                'bg-amber-50/80 border-amber-200',
                'bg-rose-50/80 border-rose-200'
              ];
              const bg = bgColors[idx % bgColors.length];
              const catName = cat.name || cat;
              return (
                <Link
                  key={cat.id || catName}
                  to={`/categories/${encodeURIComponent(catName)}/products`}
                  className={`liquid-glass rounded-3xl p-6 flex flex-col justify-between border ${bg} hover:border-orange-500/40 transition-all duration-500 group hover:-translate-y-2 shadow-sm`}
                >
                  <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Tag className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight group-hover:text-orange-600 transition-colors">
                      {catName}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">{cat.description || `Explore top quality ${catName} products`}</p>
                    <div className="mt-4 flex items-center justify-between pt-3 border-t border-slate-200">
                      <span className="text-[10px] font-black uppercase text-orange-600 tracking-wider">Explore Sector</span>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

        </div>
      </section>

      {/* ── SECTION 3: TABBED PRODUCT MATRIX ── */}
      <section className="py-12 bg-slate-100/70 border-y border-slate-200/80 relative z-10">
        <div className="max-w-7xl mx-auto px-4">
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
            <div>
              <span className="px-3.5 py-1 rounded-full bg-purple-100 text-purple-700 border border-purple-200 text-xs font-black uppercase tracking-widest">
                SPOTLIGHT MATRIX
              </span>
              <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tight mt-2">
                CURATED SELECTION
              </h2>
            </div>
          </div>

          {loading ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="w-10 h-10 text-orange-600 animate-spin" />
            </div>
          ) : topStockProducts.length === 0 ? (
            <div className="py-16 flex flex-col items-center gap-3">
              <ShoppingBag className="w-14 h-14 text-slate-300" />
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest">No Products Available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {topStockProducts.map((p, i) => {
                const stockCount = getStock(p);
                const maxStock = getStock(topStockProducts[0]) || 1;
                const stockPct = Math.min(100, Math.round((stockCount / maxStock) * 100));
                const inStock = isInStock(p);
                return (
                  <Link
                    key={p.id || i}
                    to={`/products/${p.id}`}
                    className="bg-white rounded-3xl overflow-hidden border border-slate-200 hover:border-orange-400 transition-all duration-300 group flex flex-col hover:-translate-y-2 shadow-sm hover:shadow-xl relative"
                  >
                    <div className="h-52 bg-white p-4 flex items-center justify-center relative overflow-hidden shimmer-effect">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-md product-img-clean" />
                      ) : (
                        <ShoppingBag className="w-16 h-16 text-slate-300 animate-float-bob" />
                      )}
                      <span className="absolute top-3 left-3 text-[9px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-white text-orange-600 border border-orange-200 shadow-sm">
                        {p.category}
                      </span>
                    </div>

                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-xs font-black text-slate-900 uppercase line-clamp-2 mb-2 group-hover:text-orange-600 transition-colors">
                        {p.name}
                      </h3>
                      <div className="flex items-center gap-1 mb-3 text-xs text-amber-500">
                        {[...Array(5)].map((_, s) => <Star key={s} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                        <span className="text-[11px] text-slate-500 ml-1 font-bold">({p.average_rating || 4.8})</span>
                      </div>

                      <div className="flex items-baseline gap-2 mb-4 mt-auto">
                        <span className="text-xl font-black text-slate-900">₹{(p.price || 0).toLocaleString()}</span>
                        <span className="text-xs text-slate-400 line-through">₹{Math.round((p.price || 0) * 1.2).toLocaleString()}</span>
                      </div>
                      {inStock ? (
                        <button
                          onClick={(e) => handleAddToCart(e, p)}
                          className="w-full skeuo-btn py-2.5 rounded-2xl text-white text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5"
                        >
                          <ShoppingCart className="w-4 h-4" />
                          {addedProductId === p.id ? 'ADDED ✓' : 'ADD TO CART'}
                        </button>
                      ) : (
                        <button disabled className="w-full py-2.5 bg-slate-200 text-slate-500 text-xs font-black uppercase rounded-2xl cursor-not-allowed">
                          UNAVAILABLE
                        </button>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

        </div>
      </section>

      {/* ── SECTION 5: SWISS MINIMAL LIVE PRODUCTS GRID ── */}
      <section className="py-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-200">
            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-orange-600" /> ALL LIVE INVENTORY
            </h2>
            <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{products.length} ITEMS TOTAL</span>
          </div>

          {loading ? (
            <div className="py-20 flex flex-col items-center">
              <Loader2 className="w-12 h-12 text-orange-600 animate-spin" />
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest mt-4">Loading store inventory...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((p, i) => {
                const inStock = isInStock(p);
                return (
                  <Link
                    key={p.id || i}
                    to={`/products/${p.id}`}
                    className="bg-white rounded-3xl overflow-hidden border border-slate-200 hover:border-orange-400 transition-all duration-500 group flex flex-col hover:-translate-y-2 shadow-sm hover:shadow-xl relative"
                  >
                    <div className="h-52 bg-white p-4 flex items-center justify-center relative overflow-hidden shimmer-effect">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-md product-img-clean" />
                      ) : (
                        <ShoppingBag className="w-16 h-16 text-slate-300 animate-float-bob" />
                      )}
                      <span className="absolute top-3 left-3 text-[9px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-white text-orange-600 border border-orange-200 shadow-sm">
                        {p.category}
                      </span>
                    </div>

                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-xs font-black text-slate-900 uppercase line-clamp-2 mb-2 group-hover:text-orange-600 transition-colors">
                        {p.name}
                      </h3>
                      <div className="flex items-center gap-1 mb-3 text-xs text-amber-500">
                        {[...Array(5)].map((_, s) => <Star key={s} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                        <span className="text-[11px] text-slate-500 ml-1 font-bold">({p.average_rating || 4.8})</span>
                      </div>

                      <div className="flex items-baseline gap-2 mb-4 mt-auto">
                        <span className="text-xl font-black text-slate-900">₹{(p.price || 0).toLocaleString()}</span>
                        <span className="text-xs text-slate-400 line-through">₹{Math.round((p.price || 0) * 1.2).toLocaleString()}</span>
                      </div>

                      {inStock ? (
                        <button
                          onClick={(e) => handleAddToCart(e, p)}
                          className="w-full skeuo-btn py-2.5 rounded-2xl text-white text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5"
                        >
                          <ShoppingCart className="w-4 h-4" />
                          {addedProductId === p.id ? 'ADDED ✓' : 'ADD TO CART'}
                        </button>
                      ) : (
                        <button disabled className="w-full py-2.5 bg-slate-200 text-slate-500 text-xs font-black uppercase rounded-2xl cursor-not-allowed">
                          UNAVAILABLE
                        </button>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ── SECTION 6: EXACT USER TRUST ADVANTAGE CARDS ── */}
      <section className="py-12 bg-slate-100/80 border-t border-slate-200/80 relative z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            
            {/* Card 1: Easy Monthly Plans */}
            <div className="clay-card p-5 flex items-center gap-4 bg-white border border-slate-200">
              <div className="w-12 h-12 rounded-2xl bg-blue-100 border border-blue-200 flex items-center justify-center shrink-0">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase">EASY MONTHLY PLANS</h4>
                <p className="text-[11px] font-medium text-slate-500">Budget Friendly Options</p>
              </div>
            </div>

            {/* Card 2: 0% No Cost EMI */}
            <div className="clay-card p-5 flex items-center gap-4 bg-white border border-slate-200">
              <div className="w-12 h-12 rounded-2xl bg-green-100 border border-green-200 flex items-center justify-center shrink-0">
                <Zap className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase">0% NO COST EMI</h4>
                <p className="text-[11px] font-medium text-slate-500">On Selected Products</p>
              </div>
            </div>

            {/* Card 3: 100% Trusted Products */}
            <div className="clay-card p-5 flex items-center gap-4 bg-white border border-slate-200">
              <div className="w-12 h-12 rounded-2xl bg-orange-100 border border-orange-200 flex items-center justify-center shrink-0">
                <Award className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase">100% TRUSTED PRODUCTS</h4>
                <p className="text-[11px] font-medium text-slate-500">Quality You Can Trust</p>
              </div>
            </div>

            {/* Card 4: Secured Payment */}
            <div className="clay-card p-5 flex items-center gap-4 bg-white border border-slate-200">
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 border border-emerald-200 flex items-center justify-center shrink-0">
                <Lock className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase">SECURED PAYMENT</h4>
                <p className="text-[11px] font-medium text-slate-500">100% Secured</p>
              </div>
            </div>

            {/* Card 5: Continuous Support */}
            <div className="clay-card p-5 flex items-center gap-4 bg-white border border-slate-200">
              <div className="w-12 h-12 rounded-2xl bg-indigo-100 border border-indigo-200 flex items-center justify-center shrink-0">
                <Headset className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase">CONTINUOUS SUPPORT</h4>
                <p className="text-[11px] font-medium text-slate-500">Throughout Month</p>
              </div>
            </div>

            {/* Card 6: Minimal Shipping */}
            <div className="clay-card p-5 flex items-center gap-4 bg-white border border-slate-200">
              <div className="w-12 h-12 rounded-2xl bg-purple-100 border border-purple-200 flex items-center justify-center shrink-0">
                <Truck className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase">MINIMAL SHIPPING</h4>
                <p className="text-[11px] font-medium text-slate-500">For All Orders</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── SECTION 7: LIGHT GLASS VIP TESTIMONIALS ── */}
      <section className="py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-xl mx-auto mb-12">
            <span className="px-3.5 py-1 rounded-full bg-orange-100 text-orange-700 border border-orange-200 text-xs font-black uppercase tracking-widest">
              WALL OF LOVE
            </span>
            <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tight mt-2">
              VERIFIED BUYER REVIEWS
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {[
              { name: 'Rajesh Patil', city: 'Bailhongal', quote: 'Ordered a Smart TV and received it within 24 hours. Zero cost EMI process was smooth!', rating: 5 },
              { name: 'Sunita Kulkarni', city: 'Gokak', quote: 'High quality genuine products. The refrigerator packaging was super secure.', rating: 5 },
              { name: 'Amit Desai', city: 'Belagavi', quote: 'BrightBazaar support answered all my queries instantly. Highly recommended e-com store.', rating: 5 },
            ].map((rev, i) => (
              <div key={i} className="glass-panel rounded-3xl p-6 border border-slate-200/90 relative space-y-4 bg-white/90 shadow-sm">
                <div className="flex items-center gap-1 text-amber-500">
                  {[...Array(rev.rating)].map((_, s) => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-xs text-slate-600 italic leading-relaxed">"{rev.quote}"</p>
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-black text-slate-900 uppercase">{rev.name}</p>
                    <p className="text-[10px] text-slate-500">{rev.city}, Verified Buyer</p>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FULL SCREEN CRYSTAL HD IMAGE LIGHTBOX MODAL ── */}
      {previewModalProduct && (
        <div 
          className="fixed inset-0 z-50 bg-white/70 backdrop-blur-2xl flex items-center justify-center p-4 sm:p-6 animate-fade-in"
          onClick={() => setPreviewModalProduct(null)}
        >
          <div 
            className="bg-white rounded-[2.5rem] max-w-3xl w-full p-6 sm:p-8 border border-slate-200 shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-center gap-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setPreviewModalProduct(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors z-20"
              aria-label="Close HD Preview"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Left: Full HD Large Product Image Container */}
            <div className="w-full md:w-1/2 h-72 sm:h-80 md:h-96 bg-white rounded-3xl border border-slate-200/90 p-4 flex items-center justify-center relative overflow-hidden shadow-inner">
              {previewModalProduct.image_url ? (
                <img 
                  src={previewModalProduct.image_url} 
                  alt={previewModalProduct.name}
                  className="max-h-full max-w-full object-contain drop-shadow-2xl filter contrast-[1.05] brightness-[1.02]"
                />
              ) : (
                <ShoppingBag className="w-24 h-24 text-slate-300" />
              )}
              <span className="absolute bottom-3 left-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-md">
                CRYSTAL HD DISPLAY
              </span>
            </div>

            {/* Right: Product Quick Specs & CTA */}
            <div className="w-full md:w-1/2 space-y-4 text-left">
              <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-[10px] font-black uppercase tracking-widest border border-orange-200">
                {previewModalProduct.category?.toUpperCase() || 'FEATURED SECTOR'}
              </span>
              
              <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight leading-tight">
                {previewModalProduct.name}
              </h3>

              <p className="text-xs text-slate-600 font-medium leading-relaxed line-clamp-3">
                {previewModalProduct.description || 'High-performance certified product with manufacturer warranty, instant 0% EMI options, and express delivery.'}
              </p>

              <div className="flex items-baseline gap-3 pt-2">
                <span className="text-3xl font-black text-orange-600 font-mono">
                  ₹{Number(previewModalProduct.price || 0).toLocaleString()}
                </span>
                {previewModalProduct.price && (
                  <span className="text-sm text-slate-400 line-through font-mono">
                    ₹{Math.round(previewModalProduct.price * 1.25).toLocaleString()}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
                {previewModalProduct.id && previewModalProduct.id !== 'default' && (
                  <button
                    onClick={(e) => {
                      handleAddToCart(e, previewModalProduct);
                      setPreviewModalProduct(null);
                    }}
                    className="flex-1 py-3 bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-600 text-white text-xs font-black uppercase tracking-wider rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-orange-500/20 transition-all hover:scale-[1.02]"
                  >
                    <ShoppingCart className="w-4 h-4 text-white" />
                    <span>ADD TO CART</span>
                  </button>
                )}

                <Link
                  to={previewModalProduct.id && previewModalProduct.id !== 'default' ? `/products/${previewModalProduct.id}` : (previewModalProduct.category ? `/categories/${encodeURIComponent(previewModalProduct.category)}/products` : '/')}
                  onClick={() => setPreviewModalProduct(null)}
                  className="flex-1 py-3 skeuo-btn text-white text-xs font-black uppercase tracking-wider rounded-2xl flex items-center justify-center gap-2 shadow-lg transition-transform hover:scale-105"
                >
                  <span>VIEW FULL SPECS</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StorePage;
